import React, { useState, useEffect } from "react";
import { PlayerAvatar, PlayerName, bannerStyle } from "./PlayerIdentity";
import { MedalBadge } from "./ProfileStats";
import { t, tx } from "../i18n";
import Coin from "./Coin";
import { addPendingReward } from "../pendingRewards";
import { useSounds } from "../sound";
import { showMedal } from "../medalEvents";
import { useFeatures } from "../features";

// End-of-game screen: podium, full leaderboard and — in Imposter mode — the
// reveals (who was what, who unmasked whom).
function FinalScores({ results, player, isHost, onSignup, onReplay, onReconfigure, onQuit }) {
  const [tab, setTab] = useState("classement");
  const features = useFeatures();
  const { playSuccess } = useSounds();

  // Fin de partie : une petite fanfare, une seule fois
  useEffect(() => { playSuccess(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const summary = results.imposterSummary || null;
  const tb = results.tieBreak || null;
  const myReward = results.rewards?.[player?.playerId] || null;

  // Sans compte, le gain est mis de côté (jeton signé par le serveur) : il sera
  // crédité si le joueur s'inscrit.
  useEffect(() => {
    if (myReward && !myReward.hasAccount && myReward.claimToken) {
      addPendingReward(myReward.claimToken, myReward.coins, myReward.junk);
    }
  }, [myReward?.claimToken]);
  const cur = results.currency || { coin: t("shop.coin"), junk: t("shop.junk") };

  const withIds = [...(results.players || [])].map(p => ({ ...p, playerId: p.playerId || p.id }));
  // En Battle Royale le serveur impose l'ordre (élimination), le score ne le
  // reflète pas : un joueur sorti tôt peut avoir marqué plus qu'un survivant.
  const ranked = Array.isArray(results.ranking) && results.ranking.length
    ? results.ranking
        .map(pid => withIds.find(p => p.playerId === pid))
        .filter(Boolean)
        .concat(withIds.filter(p => !results.ranking.includes(p.playerId)))
    : withIds.sort((a, b) => {
        const d = (b.score || 0) - (a.score || 0);
        if (d !== 0) return d;
        // On equal scores, the tie-break winner comes first
        if (tb?.winner === a.playerId) return -1;
        if (tb?.winner === b.playerId) return 1;
        return 0;
      });
  const podium = ranked.slice(0, 3);
  const rest = ranked.slice(3);
  const myId = player?.playerId;

  const statOf = (pid) => summary?.stats?.find(s => s.playerId === pid);
  const VARIANT_KEYS = {
    manches: "variantRounds", taupe: "variantMole",
    volontaire: "variantVolunteer", incognito: "variantIncognito",
  };
  const variantLabel = (v) => (VARIANT_KEYS[v] ? t(`finalScores.${VARIANT_KEYS[v]}`) : v);

  // One narrative line per reveal
  const eventLine = (e, i) => {
    if (e.kind === "round") {
      return e.imposterId
        ? <>{tx("finalScores.eventRound", { round: e.round + 1, name: <b>{e.pseudos.imposter}</b> })}
            {Object.values(e.votes || {}).includes(e.imposterId)
              ? t("finalScores.eventRoundCaught")
              : t("finalScores.eventRoundNeverCaught")}</>
        : <>{tx("finalScores.eventRoundNone", { round: e.round + 1, bold: <b>{t("finalScores.eventRoundNoneBold")}</b> })}</>;
    }
    if (e.kind === "reign") {
      if (!e.imposterId) return <>{t("finalScores.eventReignNone")}</>;
      return <>
        {tx("finalScores.eventReign", {
          name: <b>{e.pseudos.imposter}</b>,
          count: t("imposter.questionCount", { count: e.questions }),
        })}
        {e.caughtBy
          ? tx("finalScores.eventReignCaught", { name: <b>{e.pseudos.caughtBy}</b> })
          : t("finalScores.eventReignRetired")}
      </>;
    }
    if (e.kind === "bluff") {
      if (!e.bluffers.length) return <>{t("finalScores.eventBluffNone", { index: e.index + 1 })}</>;
      return <>{t("finalScores.eventBluff", { index: e.index + 1 })}{e.pseudos.bluffers.map((n, j) => (
        <span key={j}>{j > 0 && ", "}<b>{n}</b> {e.caught.includes(e.bluffers[j]) ? t("imposter.caught") : t("imposter.gotAway")}</span>
      ))}</>;
    }
    if (e.kind === "trap") {
      const bons = Object.values(e.bets || {}).filter(v => v === e.trappedId).length;
      return <>{tx("finalScores.eventTrap", { index: e.index + 1, name: <b>{e.pseudos.trapped}</b> })}
        {bons ? t("finalScores.eventTrapGuessed", { count: bons }) : t("finalScores.eventTrapMissed")}</>;
    }
    return null;
  };

  return (
    <div className="FinalScores">
      <h2 className="fs-title">{t("finalScores.title")}</h2>

      {/* Sans pièces (boutique fermée), l'encart reste pour les médailles. */}
      {myReward && (myReward.coins > 0 || myReward.junk > 0 || myReward.medals?.length > 0) && (
        <div className={`fs-reward${myReward.hasAccount ? "" : " anonymous"}`}>
          <div className="fs-reward-amounts">
            {myReward.coins > 0 && <span className="fs-coin"><Coin size={26} /> +{myReward.coins}</span>}
            {myReward.junk > 0 && <span className="fs-junk"><Coin kind="junk" size={26} /> +{myReward.junk}</span>}
          </div>
          {myReward.breakdown?.length > 0 && (
            <div className="fs-reward-detail">
              {myReward.breakdown.map((b, i) => (
                <span key={i}>{b.label} <b>{b.amount >= 0 ? "+" : ""}{b.amount}</b></span>
              ))}
            </div>
          )}
          {myReward.medals?.length > 0 && (
            <div className="fs-medals">
              {myReward.medals.map(m => (
                <span key={m.id} className="fs-medal medal-tap" onClick={() => showMedal({ ...m, unlocked: true })}>
                  <MedalBadge medal={{ ...m, unlocked: true }} size={22} />
                  {t("finalScores.medalUnlocked", { name: m.name })}
                </span>
              ))}
            </div>
          )}
          {(myReward.coins > 0 || myReward.junk > 0) && (myReward.hasAccount
            ? <span className="fs-reward-note">{t("finalScores.addedToWallet")}</span>
            : features.register && (
              <button type="button" className="fs-reward-note warn fs-reward-cta" onClick={onSignup}>
                {t("finalScores.createAccountToKeep", { currency: cur.coin.toLowerCase() })}
              </button>))}
        </div>
      )}

      {tb?.winnerPseudo && (
        <div className="fs-tiebreak">
          {tx("finalScores.tieBreakWinner", { name: <b>{tb.winnerPseudo}</b> })}
          {tb.history?.length > 1 && t("finalScores.tieBreakRounds", { count: tb.history.length })}
        </div>
      )}
      {tb && !tb.winner && tb.history?.length > 0 && (
        <div className="fs-tiebreak">{t("finalScores.tieBreakNone")}</div>
      )}

      {summary && (
        <div className="fs-tabs">
          <button className={`fs-tab${tab === "classement" ? " active" : ""}`} onClick={() => setTab("classement")}>{t("finalScores.tabRanking")}</button>
          <button className={`fs-tab${tab === "revelations" ? " active" : ""}`} onClick={() => setTab("revelations")}>
            {t("finalScores.tabReveals")}
          </button>
        </div>
      )}

      {tab === "classement" || !summary ? (
        <>
          <div className="fs-podium">
            {[1, 0, 2].map(pos => {
              const p = podium[pos];
              if (!p) return null;
              return (
                /* Pas de bannière sur le podium : posée sur la marche, elle
                   recouvrait le rang et le score. */
                <div key={p.playerId}
                  className={`fs-podium-slot rank-${pos + 1}${p.playerId === myId ? " me" : ""}`}>
                  <PlayerAvatar player={p} size={64} />
                  <span className="fs-podium-name">
                    <PlayerName player={p} />{tb?.winner === p.playerId && " ⚔️"}
                  </span>
                  <span className="fs-podium-score">{p.score} </span>
                  <div className="fs-podium-block">{pos + 1}</div>
                </div>
              );
            })}
          </div>
          {rest.length > 0 && (
            <ul className="fs-rest">
              {rest.map((p, i) => (
                <li key={p.playerId} style={bannerStyle(p)}
                  className={`${p.playerId === myId ? "me" : ""}${p.cosmetics?.banner ? " has-banner" : ""}`}>
                  <span className="fs-rest-pos">{i + 4}</span>
                  <PlayerAvatar player={p} size={34} />
                  <span className="fs-rest-name"><PlayerName player={p} /></span>
                  <span className="fs-rest-score">{p.score}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <div className="fs-reveal">
          <p className="fs-reveal-mode">{t("finalScores.revealMode", { variant: variantLabel(summary.variant) })}</p>

          <div className="fs-stats">
            {ranked.map(p => {
              const st = statOf(p.playerId);
              if (!st) return null;
              return (
                <div key={p.playerId} className={`fs-stat-card${p.playerId === myId ? " me" : ""}`}>
                  <PlayerAvatar player={p} size={40} />
                  <span className="fs-stat-name">{p.pseudo}</span>
                  <div className="fs-stat-lines">
                    <span>{tx("finalScores.statImposter", { count: <b>{st.timesImposter}</b> })}{st.timesImposter > 0 && t("finalScores.statCaught", { count: st.timesCaught })}</span>
                    <span>{tx("finalScores.statFound", { count: <b>{st.catches}</b>, wrong: st.wrongAccusations })}</span>
                    {(st.pointsFromRole !== 0 || st.pointsFromCatches !== 0) && (
                      <span className="fs-stat-pts">
                        {st.pointsFromRole !== 0 && t("finalScores.ptsRole", { points: `${st.pointsFromRole > 0 ? "+" : ""}${st.pointsFromRole}` })}
                        {st.pointsFromCatches !== 0 && t("finalScores.ptsInvestigation", { points: `${st.pointsFromCatches > 0 ? "+" : ""}${st.pointsFromCatches}` })}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {summary.events.length > 0 && (
            <>
              <h3 className="fs-timeline-title">{t("finalScores.timeline")}</h3>
              <ul className="fs-timeline">
                {summary.events.map((e, i) => <li key={i}>{eventLine(e, i)}</li>)}
              </ul>
            </>
          )}
        </div>
      )}

      {/* What comes next: restarting and reconfiguring commit everyone, so only
          the host decides them. Leaving stays individual. */}
      <div className="fs-actions">
        {isHost && (
          <>
            <button className="btn btn-primary" onClick={onReplay}>
              {t("finalScores.replay")}
            </button>
            <button className="btn" onClick={onReconfigure}>
              {t("finalScores.reconfigure")}
            </button>
          </>
        )}
        <button className="btn btn-outline" onClick={onQuit}>
          {t("finalScores.quit")}
        </button>
      </div>
      {!isHost && (
        <p className="fs-actions-hint">
          {t("finalScores.hostOnlyHint")}
        </p>
      )}
    </div>
  );
}

export default FinalScores;
